import React, { useEffect, useState, useRef } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const getCurrentDate = () => new Date().toISOString().split("T")[0];

const MedicineOrder = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { data: stockMedicines, isLoading } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    token
  );

  const [input, setInput] = useState({
    date: getCurrentDate(),
    items: [],
    total_price: 0,
    is_loan: false,
    name: "",
    father_name: "",
    phone_number: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");

  const {
    handleAdd: handleAddOrder,
    resetForm,
    loading,
  } = useAdd(
    "medicine-orders",
    token,
    t("pharmacy.medicineOrderCreatedSuccess"),
    t("pharmacy.failedToCreateOrder")
  );

  const autocompleteRef = useRef(null);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);

  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        items: [],
        total_price: 0,
        is_loan: false,
        name: "",
        father_name: "",
        phone_number: "",
      });
      setFormErrors({});
    }
  }, [resetForm]);

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];

    items[index][name] = value ? parseFloat(value) || 0 : 0;

    // Recalculate item total price based on quantity and price
    if (name === "quantity" || name === "sell_price") {
      const quantity = parseFloat(items[index].quantity) || 0;
      const sellPrice = parseFloat(items[index].sell_price) || 0;
      items[index].total_price = parseFloat((quantity * sellPrice).toFixed(2));
    }

    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      const sellPrice = parseFloat(medicine.sell_price) || 0;
      const newItem = {
        stock_medicine: medicine.id,
        name: medicine.medicine_display,
        quantity: 1,
        sell_price: sellPrice,
        total_price: parseFloat(sellPrice.toFixed(2)),
        available_stock: medicine.quantity,
      };

      const newItems = [...input.items, newItem];
      setInput((prevInput) => ({
        ...prevInput,
        items: newItems,
      }));
      updateTotalBill(newItems);
      setAutocompleteInputValue("");
      if (autocompleteRef.current) {
        autocompleteRef.current.focus();
      }
    }
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const updateTotalBill = (items) => {
    const totalPrice = items.reduce(
      (sum, item) => sum + (parseFloat(item.total_price) || 0),
      0
    );

    setInput((prevInput) => ({
      ...prevInput,
      total_price: parseFloat(totalPrice.toFixed(2)),
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (input.is_loan) {
      if (!input.name) {
        errors.name = t("pharmacy.nameRequired");
        isValid = false;
      }
      if (!input.father_name) {
        errors.father_name = t("pharmacy.fatherNameRequired");
        isValid = false;
      }
      if (!input.phone_number) {
        errors.phone_number = t("pharmacy.phoneRequired");
        isValid = false;
      }
    }

    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);
    if (isValid) {
      const payload = {
        date: input.date,
        items: input.items.map((item) => ({
          stock_medicine: item.stock_medicine,
          quantity: item.quantity,
        })),
        is_loan: input.is_loan,
        ...(input.is_loan && {
          name: input.name,
          father_name: input.father_name,
          phone_number: input.phone_number,
        }),
      };
      handleAddOrder(payload);
    }
  };

  const handleKeyDown = (index, fieldType, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (fieldType === "quantity") {
        if (priceRefs.current[index]) {
          priceRefs.current[index].focus();
        }
      } else if (fieldType === "sell_price") {
        if (index < input.items.length - 1) {
          if (quantityRefs.current[index + 1]) {
            quantityRefs.current[index + 1].focus();
          }
        } else {
          if (autocompleteRef.current) {
            autocompleteRef.current.focus();
          }
        }
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Filter medicines only if user has typed something.
  const filteredMedicines =
    autocompleteInputValue.length > 0 && stockMedicines
      ? stockMedicines.filter((medicine) =>
          medicine.medicine_display
            .toLowerCase()
            .startsWith(autocompleteInputValue.toLowerCase())
        )
      : [];

  return (
    <Paper
      sx={{
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        mt: 4,
        mb: 4,
        p: 6,
        width: "100%",
        mx: "auto",
      }}
    >
      {/* Go Back Icon and Title */}
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("pharmacy.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("pharmacy.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography fontWeight={600} variant="h4" color="primary" gutterBottom>
          {t("pharmacy.orderMedicines")}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.orderDate")}
            name="date"
            type="date"
            size="medium"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={input.date}
            onChange={(e) => setInput({ ...input, date: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={input.is_loan}
                onChange={(e) =>
                  setInput({ ...input, is_loan: e.target.checked })
                }
              />
            }
            label={t("pharmacy.isLoan")}
          />
        </Grid>
        {input.is_loan && (
          <>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("pharmacy.name")}
                name="name"
                value={input.name}
                onChange={(e) => setInput({ ...input, name: e.target.value })}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("pharmacy.fatherName")}
                name="father_name"
                value={input.father_name}
                onChange={(e) =>
                  setInput({ ...input, father_name: e.target.value })
                }
                error={!!formErrors.father_name}
                helperText={formErrors.father_name}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("pharmacy.phoneNumber")}
                name="phone_number"
                value={input.phone_number}
                onChange={(e) =>
                  setInput({ ...input, phone_number: e.target.value })
                }
                error={!!formErrors.phone_number}
                helperText={formErrors.phone_number}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Search Medicine Section */}
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          {t("pharmacy.addMedicine")}
        </Typography>
        <Autocomplete
          ref={autocompleteRef}
          value={null}
          inputValue={autocompleteInputValue}
          onInputChange={(event, newInputValue) => {
            setAutocompleteInputValue(newInputValue);
          }}
          onChange={(event, value) => {
            handleAddNewItem(value);
            setAutocompleteInputValue("");
          }}
          options={filteredMedicines}
          getOptionLabel={(option) => option.medicine_display}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                padding: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
                backgroundColor: "#f9f9f9",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "#eef2f5",
                },
              }}
            >
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ whiteSpace: "nowrap", color: "text.primary" }}
              >
                {option.medicine_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.category")}:</strong>{" "}
                {option.category_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                {option.expiry_date}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("pharmacy.searchMedicine")}
              variant="outlined"
              placeholder={t("pharmacy.searchMedicinePlaceholder")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
              }}
            />
          )}
        />
      </Box>

      {/* Medicine List Table */}
      <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#eef2f5" }}>
            <TableRow>
              <TableCell>{t("pharmacy.medicineName")}</TableCell>
              <TableCell align="center">
                {t("pharmacy.availableStock")}
              </TableCell>
              <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
              <TableCell align="center">{t("pharmacy.price")}</TableCell>
              <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
              <TableCell align="center">{t("pharmacy.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {input.items.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item.name}</TableCell>
                <TableCell align="center">{item.available_stock}</TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (quantityRefs.current[index] = el)}
                    name="quantity"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.quantity}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "quantity", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (priceRefs.current[index] = el)}
                    name="sell_price"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.sell_price}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "sell_price", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={600}>{item.total_price}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t("pharmacy.removeItem")} arrow>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Save Button and Total Price Display */}
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <TextField
          size="small"
          label={t("pharmacy.totalPrice")}
          type="number"
          value={input.total_price}
          disabled
          sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
        />
        <Button
          size="small"
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            fontWeight: 700,
            backgroundColor: "primary.main",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
        </Button>
      </Stack>
    </Paper>
  );
};

export default MedicineOrder;
